import { useRef, useEffect, useState } from "react";
import "./OptimizedImage.scss";

/**
 * OptimizedImage — Drop-in replacement para <img> con optimización de rendimiento.
 * Renderiza directamente un <img> sin wrapper, manteniendo compatibilidad total con CSS existente.
 *
 * - priority=true  → fetchpriority="high", loading="eager", decoding="sync"
 * - priority=false → fetchpriority="low",  loading="lazy",  decoding="async" + IntersectionObserver
 */
export const OptimizedImage = ({
    src,
    alt = "",
    className = "",
    priority = false,
    width,
    height,
    ...rest
}) => {
    const imgRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(priority ? src : undefined);

    // Para imágenes no-priority: usar IntersectionObserver para cargar cuando estén cerca del viewport
    useEffect(() => {
        if (priority) {
            setImgSrc(src);
            return;
        }

        if (!src) return;

        const img = imgRef.current;
        if (!img) {
            setImgSrc(src);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setImgSrc(src);
                    observer.disconnect();
                }
            },
            { rootMargin: "300px" }
        );

        observer.observe(img);
        return () => observer.disconnect();
    }, [src, priority]);

    return (
        <img
            ref={imgRef}
            src={imgSrc || undefined}
            alt={alt}
            className={className}
            width={width}
            height={height}
            loading={priority ? "eager" : "lazy"}
            decoding={priority ? "sync" : "async"}
            fetchPriority={priority ? "high" : "low"}
            {...rest}
        />
    );
};
